import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useUser } from '../contexts/UserContext';
import { apiService } from '../services/apiService';
import Monogram from '../components/layout/Monogram';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

interface MentorProfile {
  id: string;
  displayName: string;
  mentorType?: string;
  occupation?: string | null;
  university: string | null;
  major: string | null;
  classYear: string | null;
  isVerified: boolean;
}

interface Session {
  id: string;
  scheduledAt: string;
  duration: number;
  status: string;
  amountPaid: number;
  mentorProfile: MentorProfile;
  recordingUrl?: string | null;
  summary?: string | null;
}

type Tab = 'upcoming' | 'past' | 'cancelled';

const formatWhen = (dateString: string): string => {
  const d = new Date(dateString);
  return d.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const buildRescheduleSlots = (from: string): { label: string; iso: string }[] => {
  const base = new Date(from);
  const hours = [16.5, 10, 11];
  return hours.map((h, i) => {
    const d = new Date(base);
    d.setDate(d.getDate() + i + 1);
    d.setHours(Math.floor(h), h % 1 ? 30 : 0, 0, 0);
    return {
      iso: d.toISOString(),
      label: d.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }),
    };
  });
};

const FamilyDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('upcoming');
  const [rescheduleFor, setRescheduleFor] = useState<string | null>(null);
  const [pickedSlot, setPickedSlot] = useState<string | null>(null);
  const [rescheduleStatus, setRescheduleStatus] = useState<string>('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const fetchSessions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/mentors/my-sessions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) fetchSessions();
  }, [user, fetchSessions]);

  const now = new Date();
  const upcoming = sessions.filter(
    (s) =>
      new Date(s.scheduledAt) > now &&
      ['scheduled', 'confirmed', 'pending_confirmation'].includes(s.status)
  );
  const past = sessions.filter(
    (s) =>
      s.status === 'completed' || (new Date(s.scheduledAt) <= now && s.status !== 'cancelled')
  );
  const cancelled = sessions.filter((s) => s.status === 'cancelled');

  const canJoin = (s: Session) => {
    const mins = (new Date(s.scheduledAt).getTime() - now.getTime()) / 60000;
    return mins <= 15 && mins >= -120;
  };

  const handleRequestReschedule = async (session: Session) => {
    if (!pickedSlot) {
      setRescheduleStatus('Pick a slot first.');
      return;
    }
    const result = await apiService.rescheduleSession(session.id, pickedSlot);
    if (result.success) {
      setRescheduleStatus(
        'New time requested — the mentor confirms by email; the current time holds until then.'
      );
      fetchSessions();
    } else {
      setRescheduleStatus(result.message || result.error || 'Failed to request a new time.');
    }
  };

  if (!user) return null;

  const firstName = user.name?.split(' ')[0] || 'Your';

  return (
    <div className="section" style={{ paddingTop: 40 }}>
      <div className="wrap">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            flexWrap: 'wrap',
            gap: 16,
          }}
        >
          <div>
            <span className="eyebrow" style={{ display: 'block', marginBottom: 8 }}>
              {firstName} family
            </span>
            <h1 style={{ fontSize: 30 }}>Family dashboard</h1>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <Link className="btn btn-primary btn-sm" to="/mentors">
              Book a session
            </Link>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.5fr 1fr',
            gap: 32,
            alignItems: 'start',
            marginTop: 32,
          }}
        >
          <div>
            {/* My sessions */}
            <div className="card" style={{ marginBottom: 20 }}>
              <h3 style={{ marginBottom: 8 }}>My sessions</h3>
              <div className="tabs" style={{ marginBottom: 16 }}>
                <button className={tab === 'upcoming' ? 'on' : ''} onClick={() => setTab('upcoming')}>
                  Upcoming <span className="meta">({upcoming.length})</span>
                </button>
                <button className={tab === 'past' ? 'on' : ''} onClick={() => setTab('past')}>
                  Past <span className="meta">({past.length})</span>
                </button>
                <button
                  className={tab === 'cancelled' ? 'on' : ''}
                  onClick={() => setTab('cancelled')}
                >
                  Cancelled <span className="meta">({cancelled.length})</span>
                </button>
              </div>

              {loading ? (
                <p className="meta">Loading sessions…</p>
              ) : tab === 'upcoming' ? (
                upcoming.length === 0 ? (
                  <p className="meta">
                    No upcoming sessions.{' '}
                    <Link to="/mentors" style={{ textDecoration: 'underline' }}>
                      Browse mentors
                    </Link>{' '}
                    to book one.
                  </p>
                ) : (
                  <>
                    {upcoming.map((s) => (
                      <div
                        key={s.id}
                        style={{
                          border: '1px solid var(--hairline)',
                          borderRadius: 8,
                          padding: 16,
                          marginBottom: 12,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 16,
                            flexWrap: 'wrap',
                          }}
                        >
                          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                            <Monogram name={s.mentorProfile.displayName} />
                            <div>
                              <div style={{ fontWeight: 500 }}>
                                Session with {s.mentorProfile.displayName}
                              </div>
                              <div className="meta">
                                {formatWhen(s.scheduledAt)} ·{' '}
                                {s.status === 'confirmed'
                                  ? 'confirmed by mentor ✓'
                                  : 'awaiting mentor confirmation'}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            {canJoin(s) ? (
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => navigate(`/video-room/mentor/${s.id}`)}
                              >
                                Join session
                              </button>
                            ) : (
                              <button
                                className="btn btn-secondary btn-sm"
                                disabled
                                style={{ opacity: 0.6, cursor: 'default' }}
                              >
                                Join opens 15 min before
                              </button>
                            )}
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => navigate('/messages')}
                            >
                              Message
                            </button>
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => {
                                setRescheduleFor(rescheduleFor === s.id ? null : s.id);
                                setPickedSlot(null);
                                setRescheduleStatus('');
                              }}
                            >
                              Reschedule
                            </button>
                          </div>
                        </div>
                        {rescheduleFor === s.id && (
                          <div
                            style={{
                              borderTop: '1px solid var(--hairline)',
                              marginTop: 14,
                              paddingTop: 14,
                            }}
                          >
                            <p className="meta" style={{ marginBottom: 10 }}>
                              Pick an open slot — the mentor confirms by email and the session
                              moves.
                            </p>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              {buildRescheduleSlots(s.scheduledAt).map((slot) => (
                                <button
                                  key={slot.iso}
                                  className={`chip${pickedSlot === slot.iso ? ' on' : ''}`}
                                  onClick={() => setPickedSlot(slot.iso)}
                                >
                                  {slot.label}
                                </button>
                              ))}
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => handleRequestReschedule(s)}
                              >
                                Request new time
                              </button>
                            </div>
                            {rescheduleStatus && (
                              <p className="meta" style={{ marginTop: 8 }}>
                                {rescheduleStatus}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    <p className="meta" style={{ marginTop: 10 }}>
                      Video link activates 15 minutes before. Recording and notes arrive within an
                      hour after.
                    </p>
                  </>
                )
              ) : tab === 'past' ? (
                past.length === 0 ? (
                  <p className="meta">No past sessions yet.</p>
                ) : (
                  <table className="data">
                    <tbody>
                      {past.map((s) => (
                        <tr key={s.id}>
                          <td>
                            <div style={{ fontWeight: 500 }}>
                              Session with {s.mentorProfile.displayName}
                            </div>
                            <div className="meta">
                              {formatWhen(s.scheduledAt)} ·{' '}
                              {s.amountPaid > 0 ? `$${s.amountPaid}` : 'credit'} ·{' '}
                              {s.status === 'completed' ? 'completed' : 'ended'}
                            </div>
                          </td>
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => navigate(`/session-review/mentor/${s.id}`)}
                            >
                              Notes &amp; recording
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              ) : cancelled.length === 0 ? (
                <p className="meta">No cancelled sessions.</p>
              ) : (
                <>
                  <table className="data">
                    <tbody>
                      {cancelled.map((s) => (
                        <tr key={s.id}>
                          <td>
                            <div style={{ fontWeight: 500 }}>
                              Session with {s.mentorProfile.displayName}{' '}
                              <span className="tag">Cancelled</span>
                            </div>
                            <div className="meta">Was {formatWhen(s.scheduledAt)}</div>
                          </td>
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => navigate(`/mentors/${s.mentorProfile.id}`)}
                            >
                              Rebook
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="meta" style={{ marginTop: 10 }}>
                    Cancellations 24+ hours out return the credit or payment automatically. Later
                    than that, we review them by hand.
                  </p>
                </>
              )}
            </div>

            {/* Matches — placeholder until matching ships */}
            <div className="card warm">
              <h3 style={{ marginBottom: 4 }}>
                Matches <span className="meta">· refreshed as mentors pass verification</span>
              </h3>
              <p className="meta" style={{ marginBottom: 14 }}>
                Tell us each kid's activity and target schools and we'll surface exact-match
                mentors here as they clear verification.
              </p>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <span className="tag">Matching coming soon</span>
                <Link className="btn btn-secondary btn-sm" to="/mentors">
                  Meanwhile: browse all mentors
                </Link>
              </div>
            </div>
          </div>

          <aside>
            {/* Credits — placeholder */}
            <div className="card" style={{ marginBottom: 20 }}>
              <h3 style={{ marginBottom: 12 }}>Pack credits</h3>
              <p className="meta" style={{ marginBottom: 14 }}>
                Buy a 3- or 5-session pack on any mentor's profile and your remaining credits show
                up here — shared across the family, no payment step to rebook.
              </p>
              <Link className="btn btn-secondary btn-sm" to="/mentors">
                Find a mentor
              </Link>
            </div>

            {/* Messages */}
            <div className="card">
              <h3 style={{ marginBottom: 12 }}>Messages</h3>
              <p className="meta" style={{ marginBottom: 12 }}>
                Message mentors before and after sessions — drafts, follow-ups, and quick
                questions all live in one thread.
              </p>
              <Link
                className="meta"
                to="/messages"
                style={{ display: 'inline-block', textDecoration: 'underline' }}
              >
                Open messages →
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default FamilyDashboard;
